import c from 'classnames';

import { Icon, Typography } from '@repo/ui';

import styles from './EvaluationGroupsSummary.module.css';
import { DisplaySubMetric, EvaluationColor } from '@repo/types';

export const getEvaluationIconFromColor = (color: string) => {
  switch (color) {
    case 'green':
      return 'checkVerifiedFilled';
    case 'orange':
      return 'alertCircleFilled';
    case 'red':
      return 'alertTriangleFilled';
  }
  return 'checkFilled';
};

export const getEvaluationTextFromColor = (color: string) => {
  switch (color) {
    case 'green':
      return 'Great results';
    case 'orange':
      return 'Needs improvement';
    case 'red':
      return 'Needs support';
  }
  return 'Normal results';
};

type PieGraphProps = {
  greenPercentage: number;
  greyPercentage: number;
  orangePercentage: number;
  redPercentage: number;
};

const PieGraph = ({
  greenPercentage,
  greyPercentage,
  orangePercentage,
  redPercentage,
}: PieGraphProps) => {
  return (
    <div
      className={styles.pie}
      style={{
        background: `conic-gradient(var(--warm-grey-500) 0.00% ${greyPercentage}%, var(--green-300) ${greyPercentage}% ${
          greyPercentage + greenPercentage
        }%, var(--orange-300) ${greyPercentage + greenPercentage}% ${
          greyPercentage + greenPercentage + orangePercentage
        }%, var(--red-300) ${
          greyPercentage + greenPercentage + orangePercentage
        }%)`,
      }}
    ></div>
  );
};

export type EvaluationGroupsSummaryProps = {
  metrics: DisplaySubMetric[];
  variant?: 'simple' | 'bars' | 'graph' | 'both';
  includedByFilter?: (metric: DisplaySubMetric) => boolean;
};

const getEvaluationCount = (
  metrics: DisplaySubMetric[],
  color: EvaluationColor,
  includedByFilter?: (metric: DisplaySubMetric) => boolean,
) => {
  return metrics.filter(
    metric =>
      metric.evaluation_color === color &&
      (includedByFilter ? includedByFilter(metric) : true),
  ).length;
};

export const EvaluationGroupsSummary = ({
  metrics,
  variant = 'simple',
  includedByFilter,
}: EvaluationGroupsSummaryProps) => {
  // Calculate counts based on the filter
  const greenCount = getEvaluationCount(metrics, 'green', includedByFilter);
  const orangeCount = getEvaluationCount(metrics, 'orange', includedByFilter);
  const redCount = getEvaluationCount(metrics, 'red', includedByFilter);
  const greyCount = getEvaluationCount(metrics, 'grey', includedByFilter);

  // Total number of items regardless of filter (for display denominator X/Y)
  const staticTotal = metrics.length;

  // Total number of items *matching the current filter* (for percentage calculation)
  const filteredTotal = greenCount + orangeCount + redCount + greyCount;

  // Calculate percentages based on the *filtered* total for visual proportions
  const greenPercentage = filteredTotal > 0 ? (greenCount / filteredTotal) * 100 : 0;
  const greyPercentage = filteredTotal > 0 ? (greyCount / filteredTotal) * 100 : 0;
  const orangePercentage = filteredTotal > 0 ? (orangeCount / filteredTotal) * 100 : 0;
  const redPercentage = filteredTotal > 0 ? (redCount / filteredTotal) * 100 : 0;

  // Prevent rendering if there are no metrics at all
  if (staticTotal === 0) return <></>;

  return (
    <div className={styles.outerContainer}>
      {(variant === 'graph' || variant === 'both') && (
        <PieGraph
          // Pass percentages based on filtered total
          greenPercentage={greenPercentage}
          greyPercentage={greyPercentage}
          orangePercentage={orangePercentage}
          redPercentage={redPercentage}
        />
      )}
      <div className={styles.groupContainer}>
        <div className={styles.groupRow}>
          <Icon
            name={getEvaluationIconFromColor('green')}
            className={c(styles.green, 'my-auto')}
            size='s'
          />
          <div className={styles.evaluationLabel}>
            <Typography variant='label'>Great results</Typography>
          </div>
          {(variant === 'bars' || variant === 'both') && (
            <div className={styles.barContainer}>
              <div
                className={c(styles.bar, styles.green)}
                style={{ flexBasis: `${greenPercentage}%` }} // Use percentage based on filtered total
              />
            </div>
          )}
          <div className={styles.countLabel}>
            {/* Display filtered count over static total */}
            <Typography variant='label'>{`${greenCount}/${staticTotal}`}</Typography>
          </div>
        </div>

        <div className={styles.groupRow}>
          <Icon
            name={getEvaluationIconFromColor('grey')}
            className={c(styles.grey, 'my-auto')}
            size='s'
          />
          <div className={styles.evaluationLabel}>
            <Typography variant='label'>Normal results</Typography>
          </div>
          {(variant === 'bars' || variant === 'both') && (
            <div className={styles.barContainer}>
              <div
                className={c(styles.bar, styles.grey)}
                style={{ flexBasis: `${greyPercentage}%` }} // Use percentage based on filtered total
              />
            </div>
          )}
          <div className={styles.countLabel}>
            {/* Display filtered count over static total */}
            <Typography variant='label'>{`${greyCount}/${staticTotal}`}</Typography>
          </div>
        </div>

        <div className={styles.groupRow}>
          <Icon
            name={getEvaluationIconFromColor('orange')}
            className={c(styles.orange, 'my-auto')}
            size='s'
          />
          <div className={styles.evaluationLabel}>
            <Typography variant='label'>Needs Improving</Typography>
          </div>
          {(variant === 'bars' || variant === 'both') && (
            <div className={styles.barContainer}>
              <div
                className={c(styles.bar, styles.orange)}
                style={{ flexBasis: `${orangePercentage}%` }} // Use percentage based on filtered total
              />
            </div>
          )}
          <div className={styles.countLabel}>
            {/* Display filtered count over static total */}
            <Typography variant='label'>{`${orangeCount}/${staticTotal}`}</Typography>
          </div>
        </div>

        <div className={styles.groupRow}>
          <Icon
            name={getEvaluationIconFromColor('red')}
            className={c(styles.red, 'my-auto')}
            size='s'
          />
          <div className={styles.evaluationLabel}>
            <Typography variant='label'>Needs support</Typography>
          </div>
          {(variant === 'bars' || variant === 'both') && (
            <div className={styles.barContainer}>
              <div
                className={c(styles.bar, styles.red)}
                style={{ flexBasis: `${redPercentage}%` }} // Use percentage based on filtered total
              />
            </div>
          )}
          <div className={styles.countLabel}>
            {/* Display filtered count over static total */}
            <Typography variant='label'>{`${redCount}/${staticTotal}`}</Typography>
          </div>
        </div>
      </div>
    </div>
  );
};
